import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FiArrowLeft, FiCalendar, FiFileText, FiDownload } from "react-icons/fi";
import { Document, Page, pdfjs } from 'react-pdf';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.js';
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { useLanguage } from "../../contexts/LanguageContext";
import "../AdminPage.css";

pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;

function PeerResourceView() {
  const { resourceId } = useParams();
  const navigate = useNavigate();
  const [resource, setResource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [resourceFileUrl, setResourceFileUrl] = useState(null);
  const [resourceFileLoading, setResourceFileLoading] = useState(false);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);

  useEffect(() => {
    fetchResource();
  }, [resourceId]);

  const fetchResource = async () => {
    try {
      setLoading(true);
      const docRef = doc(db, "resources", resourceId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = { id: docSnap.id, ...docSnap.data() };
        setResource(data);
        await loadResourceFile(data);
      } else {
        setError("Resource not found");
      }
    } catch (err) {
      console.error("Error fetching resource:", err);
      setError("Failed to load resource. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const loadResourceFile = async (resourceData) => {
    try {
      setResourceFileLoading(true);
      setResourceFileUrl(null);
      setPageNumber(1);
      setNumPages(null);

      if (resourceData.fileUrl) {
        setResourceFileUrl(resourceData.fileUrl);
      }
    } catch (error) {
      console.error("Error loading resource file:", error);
    } finally {
      setResourceFileLoading(false);
    }
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return "N/A";
    let date;
    if (dateValue.toDate && typeof dateValue.toDate === 'function') {
      date = dateValue.toDate();
    } else if (dateValue instanceof Date) {
      date = dateValue;
    } else {
      date = new Date(dateValue);
    }
    if (isNaN(date.getTime())) {
      return "Invalid Date";
    }
    return date.toLocaleDateString() + " " + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const nextPage = () => {
    setPageNumber((prevPageNumber) =>
      prevPageNumber + 1 > numPages ? numPages : prevPageNumber + 1
    );
  };

  const prevPage = () => {
    setPageNumber((prevPageNumber) =>
      prevPageNumber - 1 < 1 ? 1 : prevPageNumber - 1
    );
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  if (loading) {
    return (
      <div className="content-review-page">
        <style>{`
          .admin-aura-skeleton {
            background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
            background-size: 200% 100%;
            animation: shimmer 1.5s infinite;
            border-radius: 4px;
          }
          @keyframes shimmer {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
        `}</style>
        <div className="content-review-header" style={{
          background: 'linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)',
          padding: '24px 32px',
          boxShadow: '0 4px 20px rgba(15, 23, 42, 0.3)',
          borderBottom: 'none'
        }}>
          <div className="header-left">
            <button
              className="back-btn"
              onClick={() => navigate("/peer-educator")}
              style={{
                background: 'rgba(255,255,255,0.1)',
                color: '#fff',
                borderRadius: '12px',
                border: '1px solid rgba(255,255,255,0.2)'
              }}
            >
              <FiArrowLeft />
            </button>
            <div className="header-title">
              <h1>View Resource</h1>
              <div className="submission-meta">
                <span className="submission-id-full">Resource ID: {resourceId || 'N/A'}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="content-review-body">
          <div className="content-review-main">
            <section className="article-content-section">
              <h2><FiFileText /> File Preview</h2>
              <div className="article-content">
                <div className="article-header">
                  <div className="admin-aura-skeleton" style={{ width: '65%', height: '34px', marginBottom: '16px' }} />
                  <div style={{ display: 'flex', gap: '24px', marginTop: '8px' }}>
                    <div className="admin-aura-skeleton" style={{ width: '180px', height: '18px' }} />
                    <div className="admin-aura-skeleton" style={{ width: '150px', height: '18px' }} />
                  </div>
                </div>
                <div className="admin-aura-skeleton" style={{ width: '100%', minHeight: '500px', marginTop: '20px', borderRadius: '8px' }} />
              </div>
            </section>
          </div>
        </div>
      </div>
    );
  }

  if (error || !resource) {
    return (
      <div className="content-review-page">
        <div className="error-state">
          <h3>Error</h3>
          <p>{error || "Resource not found"}</p>
          <button className="back-btn" onClick={() => navigate("/peer-educator")}>
            <FiArrowLeft /> Back to Peer Resources
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="content-review-page">
      <style>{`
        .admin-aura-skeleton {
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
          border-radius: 4px;
        }
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
      <div className="content-review-header" style={{
        background: 'linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)',
        padding: '24px 32px',
        boxShadow: '0 4px 20px rgba(15, 23, 42, 0.3)',
        borderBottom: 'none'
      }}>
        <div className="header-left">
          <button
            className="back-btn"
            onClick={() => navigate("/peer-educator")}
            style={{
              background: 'rgba(255,255,255,0.1)',
              color: '#fff',
              borderRadius: '12px',
              border: '1px solid rgba(255,255,255,0.2)'
            }}
          >
            <FiArrowLeft />
          </button>
          <div className="header-title">
            <h1>View Resource</h1>
            <div className="submission-meta">
              <span className="submission-id-full">Resource ID: {resourceId || 'N/A'}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="content-review-body">
        <div className="content-review-main">
          <section className="article-content-section">
            <h2>
              <FiFileText /> File Preview
            </h2>
            <div className="article-content">
              <div className="article-header">
                <h1 className="article-title">{resource.title}</h1>
                <div style={{ display: 'flex', gap: '24px', marginTop: '8px', color: '#6b7280' }}>
                  <span>
                    <FiCalendar style={{ marginRight: '4px' }} />
                    Created: {formatDate(resource.createdAt)}
                  </span>
                  <span>
                    Category: {resource.category}
                  </span>
                </div>
              </div>

              <div style={{ 
                minHeight: '500px', 
                border: '1px solid #e5e7eb', 
                borderRadius: '8px', 
                background: '#fff',
                marginTop: '20px'
              }}>
                {resourceFileLoading ? (
                  <div className="admin-aura-skeleton" style={{ width: '100%', minHeight: '500px', borderRadius: '8px' }} />
                ) : !resourceFileUrl ? (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '500px' }}>
                    No file available for preview
                  </div>
                ) : (
                  <>
                    {resource.type.toLowerCase() === 'pdf' && (
                      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: '500px' }}>
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'center', 
                          alignItems: 'center', 
                          gap: '16px', 
                          padding: '12px', 
                          background: '#f9fafb',
                          borderBottom: '1px solid #e5e7eb',
                          borderRadius: '8px 8px 0 0'
                        }}>
                          <button 
                            onClick={prevPage}
                            disabled={pageNumber <= 1}
                            style={{
                              padding: '8px 16px',
                              background: '#1e3a8a',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          >
                            Previous
                          </button>
                          <span>
                            Page {pageNumber} of {numPages}
                          </span>
                          <button 
                            onClick={nextPage}
                            disabled={pageNumber >= numPages}
                            style={{
                              padding: '8px 16px',
                              background: '#1e3a8a',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          >
                            Next
                          </button>
                          <a 
                            href={resourceFileUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '8px 16px',
                              background: '#28a745',
                              color: 'white',
                              borderRadius: '4px',
                              textDecoration: 'none'
                            }}
                          >
                            <FiDownload /> Download
                          </a>
                        </div>
                        <div style={{ 
                          flex: 1, 
                          overflow: 'auto', 
                          display: 'flex', 
                          justifyContent: 'center',
                          padding: '16px'
                        }}>
                          <Document 
                            file={resourceFileUrl} 
                            onLoadSuccess={onDocumentLoadSuccess}
                            loading={<div className="admin-aura-skeleton" style={{ width: '600px', maxWidth: '100%', height: '760px' }} />}
                            error={<div>Failed to load PDF</div>}
                          >
                            <Page 
                              pageNumber={pageNumber} 
                              width={600}
                              renderTextLayer={false}
                              renderAnnotationLayer={false}
                            />
                          </Document>
                        </div>
                      </div>
                    )}

                    {resource.type.toLowerCase() === 'image' && (
                      <div style={{ 
                        height: '100%', 
                        minHeight: '500px',
                        display: 'flex', 
                        flexDirection: 'column'
                      }}>
                        <div style={{ 
                          padding: '12px', 
                          background: '#f9fafb',
                          borderBottom: '1px solid #e5e7eb',
                          borderRadius: '8px 8px 0 0',
                          display: 'flex',
                          justifyContent: 'flex-end'
                        }}>
                          <a 
                            href={resourceFileUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '8px 16px',
                              background: '#28a745',
                              color: 'white',
                              borderRadius: '4px',
                              textDecoration: 'none'
                            }}
                          >
                            <FiDownload /> Download
                          </a>
                        </div>
                        <div style={{ 
                          flex: 1, 
                          overflow: 'auto', 
                          display: 'flex', 
                          justifyContent: 'center',
                          alignItems: 'center',
                          padding: '16px'
                        }}>
                          <img 
                            src={resourceFileUrl} 
                            alt={resource.title} 
                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                          />
                        </div>
                      </div>
                    )}

                    {(resource.type.toLowerCase() === 'doc' || resource.type.toLowerCase() === 'document' || resource.type.toLowerCase() === 'docx') && (
                      <div style={{ 
                        height: '100%', 
                        minHeight: '500px',
                        display: 'flex', 
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '24px'
                      }}>
                        <div style={{ textAlign: 'center' }}>
                          <FiFileText size={48} style={{ color: '#6b7280', marginBottom: '16px' }} />
                          <p style={{ color: '#6b7280', marginBottom: '16px' }}>Document preview not available</p>
                          <a 
                            href={resourceFileUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '12px 24px',
                              background: '#28a745',
                              color: 'white',
                              borderRadius: '8px',
                              textDecoration: 'none',
                              fontWeight: '600'
                            }}
                          >
                            <FiDownload /> Download Document
                          </a>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default PeerResourceView;
